import { UiMoney } from '../../types';
import { Typography, TypographyProps } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  formatPercentValue,
  formatServerMoney,
  isMoneyPositive,
  isPercentPositive,
} from '@razz-team/utils';

function concatTruthy(values: (string | null | undefined)[], separator = ' ') {
  return values.filter(Boolean).join(separator);
}

export interface PricingFeeProps extends TypographyProps {
  fee?: UiMoney | null;
  feePct?: number | null;
  minFee?: UiMoney | null;
  maxFee?: UiMoney | null;
  freeLimit?: UiMoney | null;
  freeLimitCount?: number | null;
  textBefore?: string;
  textAfter?: string;
}

export function PricingFee({
  fee,
  feePct,
  minFee,
  maxFee,
  freeLimit,
  freeLimitCount,
  textBefore,
  textAfter,
  ...props
}: PricingFeeProps) {
  const { t } = useTranslation();
  const feeText = concatTruthy(
    [
      isMoneyPositive(fee) ? formatServerMoney(fee) : null,
      isPercentPositive(feePct) ? `${formatPercentValue(feePct)}%` : null,
    ],
    ' + ',
  );
  const feeLimitText = concatTruthy(
    [
      isMoneyPositive(minFee) ? t('minimum {{value}}', { value: formatServerMoney(minFee) }) : null,
      isMoneyPositive(maxFee) ? t('maximum {{value}}', { value: formatServerMoney(maxFee) }) : null,
    ],
    ',',
  );

  const freeText = concatTruthy(
    [
      freeLimitCount && freeLimitCount > 0
        ? t('First {{count}} is free', { count: freeLimitCount })
        : null,
      isMoneyPositive(freeLimit)
        ? t('Free up to {{value}}', { value: formatServerMoney(freeLimit) })
        : null,
    ],
    ', ',
  );
  const freeWithFeeText = concatTruthy([freeText, feeText], ', then ');

  const fullFeeText = feeLimitText ? `${freeWithFeeText} (${feeLimitText})` : freeWithFeeText;

  const text = concatTruthy([textBefore, fullFeeText || t('Free'), textAfter], ' ');

  return <Typography {...props}>{text}</Typography>;
}
